/**
 * useWatchParty — WebSocket client hook for the JixxeoTV Watch Party.
 * Handles connection, chat, viewers, host-controlled channel changes.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { tvPartyWsUrl } from "@/lib/api";

export function useWatchParty({ partyId, enabled = true, onChannelChange } = {}) {
  const wsRef = useRef(null);
  const [status, setStatus] = useState("idle");   // idle | connecting | open | closed | error
  const [sid, setSid] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [state, setState] = useState({ viewers: [], chat: [], viewer_count: 0 });

  useEffect(() => {
    if (!enabled || !partyId) return;
    setStatus("connecting");
    let ws;
    try {
      ws = new WebSocket(tvPartyWsUrl(partyId));
      wsRef.current = ws;
    } catch {
      setStatus("error"); return;
    }
    ws.onopen = () => setStatus("open");
    ws.onclose = () => setStatus("closed");
    ws.onerror = () => setStatus("error");
    ws.onmessage = (evt) => {
      let d = {}; try { d = JSON.parse(evt.data); } catch { return; }
      switch (d.type) {
        case "hello":
          setSid(d.sid); setIsHost(!!d.you_are_host);
          if (d.state) {
            setState((s) => ({ ...s, ...d.state }));
            // Learn my user_id from the viewers list keyed by sid
            const me = (d.state.viewers || []).find((v) => v.sid === d.sid);
            if (me?.user_id) setMyUserId(me.user_id);
          }
          break;
        case "viewers":
          if (d.state) setState((s) => ({ ...s, viewers: d.state.viewers, viewer_count: d.state.viewer_count }));
          break;
        case "chat":
          setState((s) => ({ ...s, chat: [...s.chat, d.message].slice(-100) }));
          break;
        case "channel":
          if (d.channel && onChannelChange) onChannelChange(d.channel);
          break;
        case "host_changed":
          if (d.host_id) {
            setState((s) => ({
              ...s,
              viewers: (s.viewers || []).map((v) => ({ ...v, is_host: v.user_id === d.host_id })),
            }));
            setIsHost((prev) => (myUserId ? myUserId === d.host_id : prev));
          }
          break;
        default:
          break;
      }
    };
    return () => { try { ws?.close(); } catch {} wsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, enabled]);

  const sendChat = useCallback((text) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1 || !text.trim()) return false;
    ws.send(JSON.stringify({ type: "chat", text: text.trim().slice(0, 280) }));
    return true;
  }, []);

  const changeChannel = useCallback((channel_id) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return false;
    ws.send(JSON.stringify({ type: "change_channel", channel_id }));
    return true;
  }, []);

  const leave = useCallback(() => {
    try { wsRef.current?.close(); } catch {}
    wsRef.current = null;
  }, []);

  return { status, sid, isHost, viewers: state.viewers, chat: state.chat, viewerCount: state.viewer_count, sendChat, changeChannel, leave };
}
